import React from 'react';
import {
  IconUsers, IconArrowUpRight, IconArrowDownRight, IconBounceRight,
  IconClock, IconChartBar, IconMessages, IconTicket, IconSpeakerphone,
  IconChartArrowsVertical, IconHeadset, IconTrendingUp, IconPercentage,
  IconCurrencyShekel, IconTargetArrow, IconBrandWhatsapp, IconMail,
  IconRobot, IconUserCheck, IconMoodSmile, IconMoodSad, IconMoodEmpty,
  IconArrowsExchange, IconCalendarEvent, IconAlertTriangle, IconCircleCheck,
  IconChartDonut, IconChartLine, IconChartAreaLine, IconReportMoney
} from '@tabler/icons-react';
import { AGENTS } from '../data/industries';

// Mini bar chart component
function MiniBar({ data, color, height = 40 }) {
  const max = Math.max(...data);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height }}>
      {data.map((v, i) => (
        <div key={i} style={{
          flex: 1, background: color, opacity: 0.3 + (v / max) * 0.7,
          height: `${(v / max) * 100}%`, borderRadius: 2, minHeight: 3,
          transition: 'height 0.5s ease'
        }} />
      ))}
    </div>
  );
}

// Donut chart component
function MiniDonut({ segments, size = 80 }) {
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  let cumulative = 0;
  const gradientStops = segments.map(seg => {
    const start = (cumulative / total) * 100;
    cumulative += seg.value;
    const end = (cumulative / total) * 100;
    return `${seg.color} ${start}% ${end}%`;
  });
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: `conic-gradient(${gradientStops.join(', ')})`,
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{ width: size * 0.6, height: size * 0.6, borderRadius: '50%', background: '#fff' }} />
    </div>
  );
}

// Progress bar
function ProgressBar({ value, max, color, label, labelRight }) {
  const pct = Math.min((value / max) * 100, 100);
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
        <span style={{ color: '#374151', fontWeight: 500 }}>{label}</span>
        <span className="font-mono" style={{ color: '#6B7280' }}>{labelRight || `${value}/${max}`}</span>
      </div>
      <div style={{ height: 6, background: '#F3F4F6', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3, transition: 'width 0.8s ease' }} />
      </div>
    </div>
  );
}

export default function Dashboard({ lang, t, industry, demoPhase, demoPhone, notify }) {
  const a = industry.analytics;
  const isRtl = lang === 'ar';

  // Send a demo action message to the prospect's WhatsApp
  const sendDemoAction = async (actionAr, actionEn, contextAr, contextEn) => {
    if (!demoPhone) {
      if (notify) notify(lang === 'ar' ? 'ادخل رقم الجوال أولاً من التجربة التفاعلية' : 'Enter phone number first via Interactive Demo');
      return;
    }
    const msgAr = `${actionAr}\n\n${contextAr}`;
    const msgEn = `${actionEn}\n\n${contextEn}`;
    try {
      await fetch('http://localhost:3457/api/demo/trigger', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: demoPhone,
          name: 'Manager',
          scenario: 'action',
          industry: industry.id,
          actionMessage: lang === 'ar' ? msgAr : msgEn,
        }),
      });
      if (notify) notify(lang === 'ar' ? 'تم إرسال الإجراء!' : 'Action sent!');
    } catch (e) {
      if (notify) notify(lang === 'ar' ? 'حدث خطأ' : 'Error occurred');
    }
  };

  // Weekly data scaled to industry volume
  const scale = (a.activeConversations || 12) / 12;
  const weeklyConversations = [42, 58, 35, 67, 52, 71, 63].map(v => Math.round(v * scale));
  const weeklyDeals = [3, 5, 2, 7, 4, 6, 5].map(v => Math.round(v * scale));
  const weeklyTickets = [8, 12, 6, 9, 14, 7, 11].map(v => Math.round(v * scale));
  const weekDays = lang === 'ar'
    ? ['أحد', 'إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت']
    : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const metrics = [
    { label: t.sessionsToday, value: a.sessions, icon: IconUsers, color: '#5B2D8E', trend: '+12%', up: true },
    { label: t.conversions, value: a.conversions, icon: IconArrowUpRight, color: '#22C55E', trend: '+8%', up: true },
    { label: t.bounceRate, value: a.bounceRate + '%', icon: IconBounceRight, color: '#EF4444', trend: '-3%', up: false },
    { label: t.responseTime, value: a.responseTime, icon: IconClock, color: '#1B6B93', trend: null },
    { label: t.pipelineValue, value: a.pipelineValue, icon: IconChartBar, color: '#B5651D', trend: '+15%', up: true },
  ];

  // Agent performance data
  const agentPerformance = [
    {
      key: 'abdullah', conversations: 47, closed: 12, rate: '26%',
      avgTime: lang === 'ar' ? '٢.٣ دقيقة' : '2.3 min',
      statusAr: 'أغلق ١٢ صفقة هذا الأسبوع', statusEn: 'Closed 12 deals this week'
    },
    {
      key: 'khalid', conversations: 31, closed: 28, rate: '90%',
      avgTime: lang === 'ar' ? '١.٨ دقيقة' : '1.8 min',
      statusAr: 'حل ٢٨ تذكرة اليوم', statusEn: 'Resolved 28 tickets today'
    },
    {
      key: 'latifa', conversations: 15, closed: 3, rate: '100%',
      avgTime: lang === 'ar' ? '— حملات' : '— campaigns',
      statusAr: '٣ حملات نشطة', statusEn: '3 active campaigns'
    },
  ];

  // Channel breakdown
  const channels = [
    { name: 'WhatsApp', icon: IconBrandWhatsapp, value: 68, color: '#22C55E' },
    { name: lang === 'ar' ? 'إيميل' : 'Email', icon: IconMail, value: 18, color: '#1B6B93' },
    { name: lang === 'ar' ? 'ويب' : 'Web', icon: IconMessages, value: 14, color: '#5B2D8E' },
  ];

  // Sentiment breakdown
  const sentiments = [
    { label: lang === 'ar' ? 'إيجابي' : 'Positive', value: 64, color: '#22C55E', icon: IconMoodSmile },
    { label: lang === 'ar' ? 'محايد' : 'Neutral', value: 24, color: '#F59E0B', icon: IconMoodEmpty },
    { label: lang === 'ar' ? 'سلبي' : 'Negative', value: 12, color: '#EF4444', icon: IconMoodSad },
  ];

  // Hourly heatmap data (simplified as bars)
  const hourlyLoad = [2, 5, 8, 14, 22, 31, 28, 35, 42, 38, 29, 24, 18, 21, 26, 33, 28, 19, 12, 8, 5, 3, 2, 1];

  const summaryCards = [
    { label: t.activeConversations, value: a.activeConversations, icon: IconMessages, color: '#1B6B93' },
    { label: t.openTickets, value: industry.tickets?.filter(t => t.status === 'open').length || 0, icon: IconTicket, color: '#7B2D5F' },
    { label: t.campaignPerformance, value: industry.campaigns?.[0] ? `${industry.campaigns[0].opened}/${industry.campaigns[0].sent}` : '0/0', icon: IconSpeakerphone, color: '#B5651D' },
  ];

  const activitiesByIndustry = {
    real_estate: [
      { agent: 'abdullah', textAr: 'صفقة جديدة — شقة النرجس | ١.٥ مليون ريال', textEn: 'New deal — Narjis apartment | 1.5M SAR', time: '2m' },
      { agent: 'khalid', textAr: 'تذكرة #٤٧٢١ تم حلها — تأخير تسليم وحدة', textEn: 'Ticket #4721 resolved — unit delivery delay', time: '5m' },
      { agent: 'latifa', textAr: 'حملة المشاريع الجديدة — معدل الفتح ٤٢٪', textEn: 'New Projects campaign — 42% open rate', time: '12m' },
      { agent: 'abdullah', textAr: 'موعد معاينة تم حجزه — الأحد ١٠ ص', textEn: 'Viewing booked — Sunday 10 AM', time: '18m' },
      { agent: 'khalid', textAr: 'تنبيه: نمط شكاوى متكرر من نفس المقاول', textEn: 'Alert: Repeated complaint pattern from same contractor', time: '25m' },
      { agent: 'latifa', textAr: 'شريحة جديدة: "مهتمين بالتمويل" — ٦٣ جهة', textEn: 'New segment: "Financing Interested" — 63 contacts', time: '32m' },
      { agent: 'abdullah', textAr: 'تأهيل عميل جديد — ميزانية ٢.١ مليون', textEn: 'New lead qualified — 2.1M budget', time: '38m' },
    ],
    cleaning: [
      { agent: 'abdullah', textAr: 'حجز جديد — تنظيف عميق | ٥٥٠ ريال', textEn: 'New booking — Deep Clean | 550 SAR', time: '2m' },
      { agent: 'khalid', textAr: 'شكوى تم حلها — تأخر وصول الفريق ٢٠ دقيقة', textEn: 'Complaint resolved — crew 20 min late', time: '5m' },
      { agent: 'latifa', textAr: 'حملة الاشتراكات — ١٨ رد جديد', textEn: 'Subscription campaign — 18 new responses', time: '12m' },
      { agent: 'abdullah', textAr: 'تحويل عميل مرة واحدة لاشتراك شهري', textEn: 'One-time client converted to monthly subscription', time: '18m' },
      { agent: 'khalid', textAr: 'تنبيه: ٣ شكاوى جودة من نفس الفريق', textEn: 'Alert: 3 quality complaints from same crew', time: '25m' },
      { agent: 'latifa', textAr: 'شريحة: "عملاء منقطعين" — ٤١ جهة', textEn: 'Segment: "Churned clients" — 41 contacts', time: '32m' },
      { agent: 'abdullah', textAr: 'عقد تنظيف شهري مع مجمع سكني — ٣,٢٠٠/شهر', textEn: 'Monthly contract with residential compound — 3,200/mo', time: '38m' },
    ],
    car_dealership: [
      { agent: 'abdullah', textAr: 'صفقة جديدة — كامري ٢٠٢٦ | ١٣٥ ألف', textEn: 'New deal — 2026 Camry | 135K SAR', time: '2m' },
      { agent: 'khalid', textAr: 'شكوى تم حلها — تأخير توفر قطع غيار', textEn: 'Complaint resolved — spare parts delay', time: '5m' },
      { agent: 'latifa', textAr: 'حملة موديلات ٢٠٢٦ — معدل الفتح ٣٨٪', textEn: '2026 Models campaign — 38% open rate', time: '12m' },
      { agent: 'abdullah', textAr: 'تجربة قيادة محجوزة — الخميس ٤ م', textEn: 'Test drive booked — Thursday 4 PM', time: '18m' },
      { agent: 'khalid', textAr: 'تنبيه: ٥ شكاوى تأخير صيانة هالأسبوع', textEn: 'Alert: 5 maintenance delay complaints this week', time: '25m' },
      { agent: 'latifa', textAr: 'شريحة: "مهتمين بالتقسيط" — ٨٩ جهة', textEn: 'Segment: "Financing leads" — 89 contacts', time: '32m' },
      { agent: 'abdullah', textAr: 'تأهيل عميل — ميزانية ٢٠٠ ألف، تقسيط', textEn: 'Lead qualified — 200K budget, financing', time: '38m' },
    ],
    insurance: [
      { agent: 'abdullah', textAr: 'وثيقة جديدة — تأمين شامل | ٣,٨٠٠ ريال', textEn: 'New policy — Comprehensive | 3,800 SAR', time: '2m' },
      { agent: 'khalid', textAr: 'مطالبة تم معالجتها — حادث بسيط', textEn: 'Claim processed — minor accident', time: '5m' },
      { agent: 'latifa', textAr: 'حملة تجديد الوثائق — ٢٢ تجديد', textEn: 'Policy renewal campaign — 22 renewals', time: '12m' },
      { agent: 'abdullah', textAr: 'موعد استشارة محجوز — بكرة ١١ ص', textEn: 'Consultation booked — tomorrow 11 AM', time: '18m' },
      { agent: 'khalid', textAr: 'تنبيه: بطء معالجة مطالبات من شركة التعويض', textEn: 'Alert: Slow claim processing from adjuster company', time: '25m' },
      { agent: 'latifa', textAr: 'شريحة: "وثائق تنتهي خلال ٣٠ يوم" — ٥٦ عميل', textEn: 'Segment: "Expiring in 30 days" — 56 clients', time: '32m' },
      { agent: 'abdullah', textAr: 'تأهيل عميل — تأمين أسطول ١٢ سيارة', textEn: 'Lead qualified — fleet insurance, 12 vehicles', time: '38m' },
    ],
    retail: [
      { agent: 'abdullah', textAr: 'طلب جديد — عطر Oud Wood | ١,٢٠٠ ريال', textEn: 'New order — Oud Wood fragrance | 1,200 SAR', time: '2m' },
      { agent: 'khalid', textAr: 'شكوى تم حلها — توصيل متأخر ساعتين', textEn: 'Complaint resolved — delivery 2 hours late', time: '5m' },
      { agent: 'latifa', textAr: 'حملة استرجاع السلات — ١٥ طلب مكتمل', textEn: 'Cart recovery campaign — 15 orders completed', time: '12m' },
      { agent: 'abdullah', textAr: 'طلب جملة من عميل تجاري — ٨,٥٠٠ ريال', textEn: 'Bulk order from business client — 8,500 SAR', time: '18m' },
      { agent: 'khalid', textAr: 'تنبيه: ٨٩ سلة متروكة هالأسبوع', textEn: 'Alert: 89 abandoned carts this week', time: '25m' },
      { agent: 'latifa', textAr: 'شريحة: "عملاء VIP" — ٢٣ جهة اتصال', textEn: 'Segment: "VIP customers" — 23 contacts', time: '32m' },
      { agent: 'abdullah', textAr: 'تأهيل عميل — ميزانية شهرية ٥ آلاف', textEn: 'Lead qualified — 5K monthly budget', time: '38m' },
    ],
    furniture: [
      { agent: 'abdullah', textAr: 'صفقة جديدة — كنبة L | ١٢,٠٠٠ ريال', textEn: 'New deal — L-shaped sofa | 12,000 SAR', time: '2m' },
      { agent: 'khalid', textAr: 'شكوى تم حلها — تأخير تركيب غرفة نوم', textEn: 'Complaint resolved — bedroom set installation delay', time: '5m' },
      { agent: 'latifa', textAr: 'حملة الأثاث الجديد — معدل الفتح ٣٥٪', textEn: 'New Arrivals campaign — 35% open rate', time: '12m' },
      { agent: 'abdullah', textAr: 'زيارة معرض محجوزة — بكرة ٥ م', textEn: 'Showroom visit booked — tomorrow 5 PM', time: '18m' },
      { agent: 'khalid', textAr: 'تنبيه: تأخير توصيل من المصنع — ٤ طلبات', textEn: 'Alert: Factory delivery delay — 4 orders', time: '25m' },
      { agent: 'latifa', textAr: 'شريحة: "متصفحين الكنب" — ٤٧ زائر بدون تواصل', textEn: 'Segment: "Sofa browsers" — 47 visitors no contact', time: '32m' },
      { agent: 'abdullah', textAr: 'تأهيل عميل — تأثيث فيلا كاملة ٨٠ ألف', textEn: 'Lead qualified — full villa furnishing 80K', time: '38m' },
    ],
    fnb: [
      { agent: 'abdullah', textAr: 'طلب تموين — باقة ذهبية | ٦,٠٠٠ ريال', textEn: 'Catering order — Gold package | 6,000 SAR', time: '2m' },
      { agent: 'khalid', textAr: 'شكوى تم حلها — جودة تغليف التوصيل', textEn: 'Complaint resolved — delivery packaging quality', time: '5m' },
      { agent: 'latifa', textAr: 'حملة قائمة رمضان — ٦٧ طلب جديد', textEn: 'Ramadan Menu campaign — 67 new orders', time: '12m' },
      { agent: 'abdullah', textAr: 'حجز تموين — عزيمة ٥٠ شخص الجمعة', textEn: 'Catering booking — 50-person event Friday', time: '18m' },
      { agent: 'khalid', textAr: 'تنبيه: شكاوى التوصيل المسائي ارتفعت ٢٠٪', textEn: 'Alert: Evening delivery complaints up 20%', time: '25m' },
      { agent: 'latifa', textAr: 'شريحة: "عملاء تموين متكررين" — ١٨ جهة', textEn: 'Segment: "Repeat catering clients" — 18 contacts', time: '32m' },
      { agent: 'abdullah', textAr: 'عقد تموين أسبوعي — شركة | ١٢ ألف/شهر', textEn: 'Weekly catering contract — company | 12K/mo', time: '38m' },
    ],
    physio: [
      { agent: 'abdullah', textAr: 'موعد تقييم جديد — آلام أسفل الظهر | ٢٥٠ ريال', textEn: 'New assessment — lower back pain | 250 SAR', time: '2m' },
      { agent: 'khalid', textAr: 'شكوى تم حلها — إلغاء موعد بدون إشعار', textEn: 'Complaint resolved — appointment cancelled without notice', time: '5m' },
      { agent: 'latifa', textAr: 'حملة متابعة المرضى المنقطعين — ١٢ عودة', textEn: 'Dropped patient follow-up — 12 returned', time: '12m' },
      { agent: 'abdullah', textAr: 'باقة ١٠ جلسات محجوزة — ٢,٠٠٠ ريال', textEn: '10-session package booked — 2,000 SAR', time: '18m' },
      { agent: 'khalid', textAr: 'تنبيه: ٣٤ مريض انقطعوا عن الجلسات', textEn: 'Alert: 34 patients dropped their sessions', time: '25m' },
      { agent: 'latifa', textAr: 'شريحة: "مرضى بحاجة متابعة" — ٢٨ مريض', textEn: 'Segment: "Need follow-up" — 28 patients', time: '32m' },
      { agent: 'abdullah', textAr: 'إحالة من طبيب — ٣ مرضى جدد', textEn: 'Doctor referral — 3 new patients', time: '38m' },
    ],
    derma: [
      { agent: 'abdullah', textAr: 'استشارة جديدة — إزالة تصبغات بالليزر', textEn: 'New consultation — laser pigmentation removal', time: '2m' },
      { agent: 'khalid', textAr: 'شكوى تم حلها — نتائج غير مرضية بعد الجلسة', textEn: 'Complaint resolved — unsatisfactory post-session results', time: '5m' },
      { agent: 'latifa', textAr: 'حملة العناية بالبشرة — معدل الفتح ٤٨٪', textEn: 'Skincare campaign — 48% open rate', time: '12m' },
      { agent: 'abdullah', textAr: 'حزمة ليزر ٤ جلسات محجوزة — ٢,٥٦٠ ريال', textEn: '4-session laser package booked — 2,560 SAR', time: '18m' },
      { agent: 'khalid', textAr: 'تنبيه: ٤٥ استشارة لم تكتمل هالشهر', textEn: 'Alert: 45 incomplete consultations this month', time: '25m' },
      { agent: 'latifa', textAr: 'شريحة: "استشارات بدون حجز" — ٣٢ عميلة', textEn: 'Segment: "Consulted, not booked" — 32 clients', time: '32m' },
      { agent: 'abdullah', textAr: 'تأهيل عميلة — باقة تجميل شاملة ٥ آلاف', textEn: 'Lead qualified — full beauty package 5K', time: '38m' },
    ],
    pharmacy: [
      { agent: 'abdullah', textAr: 'طلب جديد — فيتامين د + زنك | ١٢٠ ريال', textEn: 'New order — Vitamin D + Zinc | 120 SAR', time: '2m' },
      { agent: 'khalid', textAr: 'تذكرة #٥٠١ تم حلها — تأخير وصفة طبية', textEn: 'Ticket #501 resolved — prescription delay', time: '5m' },
      { agent: 'latifa', textAr: 'حملة توعية السكري — معدل الفتح ٤٤٪', textEn: 'Diabetes awareness campaign — 44% open rate', time: '12m' },
      { agent: 'abdullah', textAr: 'طلب جملة من مستوصف — ٨,٥٠٠ ريال', textEn: 'Bulk order from clinic — 8,500 SAR', time: '18m' },
      { agent: 'khalid', textAr: 'تنبيه: ٣ أدوية أساسية نفدت من المخزون', textEn: 'Alert: 3 essential medications out of stock', time: '25m' },
      { agent: 'latifa', textAr: 'شريحة: "مرضى مزمنين" — ١٢٠ مريض', textEn: 'Segment: "Chronic patients" — 120 patients', time: '32m' },
      { agent: 'abdullah', textAr: 'عقد توريد شهري مع شركة تأمين — ١٥ ألف/شهر', textEn: 'Monthly supply contract with insurer — 15K/mo', time: '38m' },
    ],
    tourism: [
      { agent: 'abdullah', textAr: 'حجز جديد — باقة تركيا الذهبية | ٣٦,٨٠٠ ريال', textEn: 'New booking — Turkey Gold Package | 36,800 SAR', time: '2m' },
      { agent: 'khalid', textAr: 'تذكرة #٦٠١ تم حلها — فندق لا يطابق الوصف', textEn: 'Ticket #601 resolved — hotel doesn\'t match description', time: '5m' },
      { agent: 'latifa', textAr: 'حملة عروض الصيف — معدل الفتح ٤١٪', textEn: 'Summer deals campaign — 41% open rate', time: '12m' },
      { agent: 'abdullah', textAr: 'حجز مجموعة شركات — ٢٠ شخص بالي', textEn: 'Corporate group booking — 20 pax Bali', time: '18m' },
      { agent: 'khalid', textAr: 'تنبيه: ٤ شكاوى فنادق جورجيا من نفس الوكيل', textEn: 'Alert: 4 Georgia hotel complaints from same agent', time: '25m' },
      { agent: 'latifa', textAr: 'شريحة: "مسافرين عائليين" — ١٨٠ عميل', textEn: 'Segment: "Family travelers" — 180 customers', time: '32m' },
      { agent: 'abdullah', textAr: 'تأهيل عميل شهر عسل — المالديف ٢٨,٥٠٠ ريال', textEn: 'Honeymoon lead qualified — Maldives 28,500 SAR', time: '38m' },
    ],
    saas: [
      { agent: 'abdullah', textAr: 'صفقة جديدة — مطاعم مناقيش × ١٢ جهاز POS | ٤٢,٦٦٠ ر.س ARR', textEn: 'New deal — Manakish Chain × 12 terminals | 42,660 SAR ARR', time: '2m' },
      { agent: 'khalid', textAr: 'تذكرة #٩٠٢ مصعّدة — ٣ أجهزة مفصولة، حادث شريحة 4G بجدة', textEn: 'Ticket #902 escalated — 3 terminals offline, 4G SIM incident in Jeddah', time: '5m' },
      { agent: 'latifa', textAr: 'حملة تفعيل Apple Pay مجدولة — ٣٢٠ تاجر', textEn: 'Apple Pay activation campaign scheduled — 320 merchants', time: '12m' },
      { agent: 'abdullah', textAr: 'ترقية Enterprise — مطعم القرني ٢٠ جهاز سنوي ١٧٠,٤٠٠ ر.س', textEn: 'Enterprise upgrade — Al-Qarni Restaurant 20 terminals annual 170,400 SAR', time: '18m' },
      { agent: 'khalid', textAr: 'تنبيه: ١٢ تذكرة انقطاع أجهزة في ٤٨ ساعة من منطقة جدة', textEn: 'Alert: 12 terminal offline tickets in 48h from Jeddah area', time: '25m' },
      { agent: 'latifa', textAr: 'شريحة: "تجاوزوا حد العمليات" — ٨٤ تاجر', textEn: 'Segment: "At Transaction Limit" — 84 merchants', time: '32m' },
      { agent: 'abdullah', textAr: 'تأهيل Enterprise — بنك الأهلي حل POS بعلامة بيضاء | ٥٢٠ ألف ARR', textEn: 'Enterprise lead qualified — Al Ahli Bank white-label POS | 520K ARR', time: '38m' },
    ],
  };
  const activities = activitiesByIndustry[industry.id] || activitiesByIndustry.cleaning;

  return (
    <div style={{ animation: 'fadeIn 0.4s ease' }}>
      {/* KPI Metric cards - row 1 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 20 }}>
        {metrics.map((m, i) => {
          const Icon = m.icon;
          return (
            <div key={i} style={{
              background: '#fff', borderRadius: 12, padding: '20px', border: '1px solid #E5E7EB',
              animation: `slideUp 0.4s ease ${i * 0.08}s both`
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10, background: m.color + '15',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <Icon size={20} color={m.color} stroke={1.8} />
                </div>
                {m.trend && (
                  <span style={{
                    fontSize: 12, fontWeight: 600, color: m.up ? '#22C55E' : '#EF4444',
                    display: 'flex', alignItems: 'center', gap: 2
                  }}>
                    {m.up ? <IconArrowUpRight size={14} /> : <IconArrowDownRight size={14} />}
                    {m.trend}
                  </span>
                )}
              </div>
              <div className="font-mono" style={{ fontSize: 24, fontWeight: 700, color: '#1A1A2E', marginBottom: 4 }}>
                {m.value}
              </div>
              <div style={{ fontSize: 13, color: '#6B7280' }}>{m.label}</div>
            </div>
          );
        })}
      </div>

      {/* Row 2: Agent Performance + Channel Mix + Sentiment */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
        {/* Agent Performance */}
        <div style={{
          background: '#fff', borderRadius: 12, padding: '20px', border: '1px solid #E5E7EB',
          animation: 'slideUp 0.4s ease 0.4s both'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <IconRobot size={18} color="#5B2D8E" stroke={1.8} />
            <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>
              {lang === 'ar' ? 'أداء الوكلاء' : 'Agent Performance'}
            </h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {agentPerformance.map((ap) => {
              const agent = AGENTS[ap.key];
              return (
                <div key={ap.key} style={{
                  display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px',
                  background: agent.bg, borderRadius: 10, border: `1px solid ${agent.color}22`
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 8, background: agent.color + '22',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                  }}>
                    {ap.key === 'abdullah' && <IconChartArrowsVertical size={18} color={agent.color} />}
                    {ap.key === 'khalid' && <IconHeadset size={18} color={agent.color} />}
                    {ap.key === 'latifa' && <IconSpeakerphone size={18} color={agent.color} />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ fontWeight: 600, fontSize: 14, color: agent.color }}>
                        {lang === 'ar' ? agent.nameAr : agent.name}
                      </span>
                      <span className="font-mono" style={{ fontSize: 12, color: '#6B7280' }}>
                        {ap.avgTime}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 6 }}>
                      {lang === 'ar' ? ap.statusAr : ap.statusEn}
                    </div>
                    <div style={{ display: 'flex', gap: 16, fontSize: 12 }}>
                      <span className="font-mono" style={{ color: '#374151' }}>
                        <strong>{ap.conversations}</strong> {lang === 'ar' ? 'محادثة' : 'chats'}
                      </span>
                      <span className="font-mono" style={{ color: '#22C55E' }}>
                        <strong>{ap.closed}</strong> {lang === 'ar' ? 'مغلقة' : 'closed'}
                      </span>
                      <span className="font-mono" style={{ color: '#5B2D8E' }}>
                        {ap.rate} {lang === 'ar' ? 'معدل' : 'rate'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Channel Breakdown */}
        <div style={{
          background: '#fff', borderRadius: 12, padding: '20px', border: '1px solid #E5E7EB',
          animation: 'slideUp 0.4s ease 0.5s both'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <IconArrowsExchange size={18} color="#5B2D8E" stroke={1.8} />
            <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>
              {lang === 'ar' ? 'القنوات' : 'Channels'}
            </h3>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
            <MiniDonut segments={channels.map(c => ({ value: c.value, color: c.color }))} size={90} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {channels.map((ch, i) => {
              const Icon = ch.icon;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Icon size={16} color={ch.color} stroke={1.8} />
                  <span style={{ flex: 1, fontSize: 13, color: '#374151' }}>{ch.name}</span>
                  <span className="font-mono" style={{ fontSize: 14, fontWeight: 700, color: ch.color }}>{ch.value}%</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Customer Sentiment */}
        <div style={{
          background: '#fff', borderRadius: 12, padding: '20px', border: '1px solid #E5E7EB',
          animation: 'slideUp 0.4s ease 0.55s both'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <IconMoodSmile size={18} color="#5B2D8E" stroke={1.8} />
            <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>
              {lang === 'ar' ? 'رضا العملاء' : 'Sentiment'}
            </h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {sentiments.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <Icon size={18} color={s.color} stroke={1.8} />
                    <span style={{ fontSize: 13, color: '#374151', flex: 1 }}>{s.label}</span>
                    <span className="font-mono" style={{ fontSize: 14, fontWeight: 700, color: s.color }}>{s.value}%</span>
                  </div>
                  <div style={{ height: 6, background: '#F3F4F6', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', width: `${s.value}%`, background: s.color, borderRadius: 3,
                      transition: 'width 0.8s ease'
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{
            marginTop: 16, padding: '10px 12px', background: '#F0FDF4', borderRadius: 8,
            fontSize: 12, color: '#166534', display: 'flex', alignItems: 'center', gap: 6
          }}>
            <IconCircleCheck size={14} stroke={2} />
            {lang === 'ar' ? 'رضا العملاء: ٩٢٪ — أعلى من الشهر الماضي' : 'Customer satisfaction: 92% — up from last month'}
          </div>
        </div>
      </div>

      {/* Row 3: Weekly Trends + Hourly Load */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 20 }}>
        {/* Weekly Trends */}
        <div style={{
          background: '#fff', borderRadius: 12, padding: '20px', border: '1px solid #E5E7EB',
          animation: 'slideUp 0.4s ease 0.6s both'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <IconChartAreaLine size={18} color="#5B2D8E" stroke={1.8} />
            <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>
              {lang === 'ar' ? 'النشاط الأسبوعي' : 'Weekly Activity'}
            </h3>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
            <div>
              <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 8 }}>
                {lang === 'ar' ? 'المحادثات' : 'Conversations'}
              </div>
              <MiniBar data={weeklyConversations} color="#5B2D8E" height={50} />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                {weekDays.map((d, i) => (
                  <span key={i} style={{ fontSize: 9, color: '#9CA3AF', flex: 1, textAlign: 'center' }}>{d}</span>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 8 }}>
                {lang === 'ar' ? 'الصفقات' : 'Deals'}
              </div>
              <MiniBar data={weeklyDeals} color="#22C55E" height={50} />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                {weekDays.map((d, i) => (
                  <span key={i} style={{ fontSize: 9, color: '#9CA3AF', flex: 1, textAlign: 'center' }}>{d}</span>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 8 }}>
                {lang === 'ar' ? 'التذاكر' : 'Tickets'}
              </div>
              <MiniBar data={weeklyTickets} color="#EF4444" height={50} />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                {weekDays.map((d, i) => (
                  <span key={i} style={{ fontSize: 9, color: '#9CA3AF', flex: 1, textAlign: 'center' }}>{d}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Hourly Load */}
        <div style={{
          background: '#fff', borderRadius: 12, padding: '20px', border: '1px solid #E5E7EB',
          animation: 'slideUp 0.4s ease 0.65s both'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <IconClock size={18} color="#5B2D8E" stroke={1.8} />
            <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>
              {lang === 'ar' ? 'الحمل بالساعة' : 'Hourly Load'}
            </h3>
          </div>
          <MiniBar data={hourlyLoad} color="#7B4FAE" height={70} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
            <span className="font-mono" style={{ fontSize: 9, color: '#9CA3AF' }}>12AM</span>
            <span className="font-mono" style={{ fontSize: 9, color: '#9CA3AF' }}>6AM</span>
            <span className="font-mono" style={{ fontSize: 9, color: '#9CA3AF' }}>12PM</span>
            <span className="font-mono" style={{ fontSize: 9, color: '#9CA3AF' }}>6PM</span>
            <span className="font-mono" style={{ fontSize: 9, color: '#9CA3AF' }}>11PM</span>
          </div>
          <div style={{
            marginTop: 12, padding: '8px 10px', background: '#F7F2FC', borderRadius: 6,
            fontSize: 11, color: '#5B2D8E', display: 'flex', alignItems: 'center', gap: 6
          }}>
            <IconTrendingUp size={14} />
            {lang === 'ar' ? 'الذروة: ٩-١١ ص (٤٢ محادثة/ساعة)' : 'Peak: 9-11 AM (42 chats/hr)'}
          </div>
        </div>
      </div>

      {/* Row 4: Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 20 }}>
        {summaryCards.map((c, i) => {
          const Icon = c.icon;
          return (
            <div key={i} style={{
              background: '#fff', borderRadius: 12, padding: '20px', border: '1px solid #E5E7EB',
              display: 'flex', alignItems: 'center', gap: 16, animation: `slideUp 0.4s ease ${0.7 + i * 0.08}s both`
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: 12, background: c.color + '15',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <Icon size={24} color={c.color} stroke={1.8} />
              </div>
              <div>
                <div className="font-mono" style={{ fontSize: 28, fontWeight: 700, color: '#1A1A2E' }}>{c.value}</div>
                <div style={{ fontSize: 13, color: '#6B7280' }}>{c.label}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Latifa recommendation */}
      <div style={{
        background: '#FFF5EB', borderRadius: 12, padding: '16px 20px', marginBottom: 20,
        border: '1px solid #FDDCB5', display: 'flex', alignItems: 'flex-start', gap: 12,
        animation: 'slideUp 0.4s ease 0.8s both'
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10, background: '#B5651D22',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
        }}>
          <IconSpeakerphone size={18} color="#B5651D" stroke={1.8} />
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#B5651D', marginBottom: 4 }}>{t.recommendation}</div>
          <div style={{ fontSize: 14, color: '#92400E', lineHeight: 1.6 }}>
            {lang === 'ar' ? industry.recommendationAr : industry.recommendationEn}
          </div>
        </div>
      </div>

      {/* Row 5: Sentiment Deep Dive (full width) */}
      <div style={{
        background: '#fff', borderRadius: 12, padding: '20px', border: '1px solid #E5E7EB',
        marginBottom: 20, animation: 'slideUp 0.4s ease 0.85s both'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
          <IconMoodSmile size={18} color="#5B2D8E" stroke={1.8} />
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>
            {lang === 'ar' ? 'تحليل المشاعر المتقدم' : 'Advanced Sentiment Analysis'}
          </h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16 }}>
          {/* Sentiment by Agent */}
          <div>
            <div style={{ fontSize: 12, color: '#6B7280', fontWeight: 600, marginBottom: 12 }}>
              {lang === 'ar' ? 'المشاعر حسب الوكيل' : 'Sentiment by Agent'}
            </div>
            {[
              { agent: 'abdullah', positive: 72, neutral: 20, negative: 8 },
              { agent: 'khalid', positive: 58, neutral: 22, negative: 20 },
              { agent: 'latifa', positive: 85, neutral: 12, negative: 3 },
            ].map((row) => {
              const agent = AGENTS[row.agent];
              return (
                <div key={row.agent} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, color: agent.color }}>{lang === 'ar' ? agent.nameAr : agent.name}</span>
                    <span className="font-mono" style={{ color: '#22C55E', fontSize: 11 }}>{row.positive}%</span>
                  </div>
                  <div style={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: `${row.positive}%`, background: '#22C55E' }} />
                    <div style={{ width: `${row.neutral}%`, background: '#F59E0B' }} />
                    <div style={{ width: `${row.negative}%`, background: '#EF4444' }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Sentiment Over Time */}
          <div>
            <div style={{ fontSize: 12, color: '#6B7280', fontWeight: 600, marginBottom: 12 }}>
              {lang === 'ar' ? 'تطور الرضا (أسبوعي)' : 'Satisfaction Trend (Weekly)'}
            </div>
            <MiniBar data={[78, 82, 79, 85, 88, 91, 92]} color="#22C55E" height={60} />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
              {(lang === 'ar' ? ['ع١', 'ع٢', 'ع٣', 'ع٤', 'ع٥', 'ع٦', 'ع٧'] : ['W1', 'W2', 'W3', 'W4', 'W5', 'W6', 'W7']).map((w, i) => (
                <span key={i} className="font-mono" style={{ fontSize: 9, color: '#9CA3AF' }}>{w}</span>
              ))}
            </div>
            <div style={{ marginTop: 8, fontSize: 11, color: '#22C55E', display: 'flex', alignItems: 'center', gap: 4 }}>
              <IconArrowUpRight size={12} /> {lang === 'ar' ? 'تحسن ١٤٪ خلال ٧ أسابيع' : '14% improvement over 7 weeks'}
            </div>
          </div>

          {/* Top Complaint Topics */}
          <div>
            <div style={{ fontSize: 12, color: '#6B7280', fontWeight: 600, marginBottom: 12 }}>
              {lang === 'ar' ? 'أكثر المواضيع سلبية' : 'Top Complaint Topics'}
            </div>
            {(({
              real_estate: [
                { ar: 'تأخير الصيانة', en: 'Maintenance delays', count: 23, pct: 38 },
                { ar: 'عدم الرد', en: 'No response', count: 14, pct: 23 },
                { ar: 'جودة الخدمة', en: 'Service quality', count: 11, pct: 18 },
                { ar: 'أخطاء الفواتير', en: 'Billing errors', count: 8, pct: 13 },
                { ar: 'أخرى', en: 'Other', count: 5, pct: 8 },
              ],
              pharmacy: [
                { ar: 'نفاد الأدوية', en: 'Out of stock medications', count: 19, pct: 32 },
                { ar: 'تأخير الوصفات', en: 'Prescription delays', count: 16, pct: 27 },
                { ar: 'أسعار مرتفعة', en: 'High prices', count: 10, pct: 17 },
                { ar: 'خطأ في الصرف', en: 'Dispensing errors', count: 9, pct: 15 },
                { ar: 'أخرى', en: 'Other', count: 5, pct: 9 },
              ],
              cleaning: [
                { ar: 'تأخر الفريق', en: 'Crew arriving late', count: 18, pct: 30 },
                { ar: 'جودة التنظيف', en: 'Cleaning quality', count: 15, pct: 25 },
                { ar: 'عدم الرد', en: 'No response', count: 12, pct: 20 },
                { ar: 'أسعار', en: 'Pricing issues', count: 10, pct: 17 },
                { ar: 'أخرى', en: 'Other', count: 5, pct: 8 },
              ],
              car_dealership: [
                { ar: 'تأخير الصيانة', en: 'Service delays', count: 20, pct: 33 },
                { ar: 'توفر قطع الغيار', en: 'Parts availability', count: 14, pct: 23 },
                { ar: 'أسعار الخدمة', en: 'Service pricing', count: 12, pct: 20 },
                { ar: 'جودة العمل', en: 'Work quality', count: 9, pct: 15 },
                { ar: 'أخرى', en: 'Other', count: 5, pct: 9 },
              ],
              insurance: [
                { ar: 'بطء المطالبات', en: 'Slow claims processing', count: 22, pct: 37 },
                { ar: 'رفض التغطية', en: 'Coverage denials', count: 13, pct: 22 },
                { ar: 'عدم الوضوح', en: 'Unclear terms', count: 11, pct: 18 },
                { ar: 'تأخر التجديد', en: 'Renewal delays', count: 8, pct: 13 },
                { ar: 'أخرى', en: 'Other', count: 6, pct: 10 },
              ],
              retail: [
                { ar: 'تأخر الشحن', en: 'Shipping delays', count: 25, pct: 42 },
                { ar: 'منتج مختلف', en: 'Wrong product', count: 12, pct: 20 },
                { ar: 'سياسة الاسترجاع', en: 'Return policy', count: 10, pct: 17 },
                { ar: 'عدم الرد', en: 'No response', count: 8, pct: 13 },
                { ar: 'أخرى', en: 'Other', count: 5, pct: 8 },
              ],
              furniture: [
                { ar: 'تأخير التوصيل', en: 'Delivery delays', count: 21, pct: 35 },
                { ar: 'جودة المنتج', en: 'Product quality', count: 14, pct: 23 },
                { ar: 'خطأ بالطلب', en: 'Order errors', count: 11, pct: 18 },
                { ar: 'التركيب', en: 'Installation issues', count: 9, pct: 15 },
                { ar: 'أخرى', en: 'Other', count: 5, pct: 9 },
              ],
              fnb: [
                { ar: 'تأخر التوصيل', en: 'Late delivery', count: 20, pct: 33 },
                { ar: 'جودة الطعام', en: 'Food quality', count: 15, pct: 25 },
                { ar: 'طلب خاطئ', en: 'Wrong order', count: 12, pct: 20 },
                { ar: 'التغليف', en: 'Packaging', count: 8, pct: 13 },
                { ar: 'أخرى', en: 'Other', count: 5, pct: 9 },
              ],
              physio: [
                { ar: 'إلغاء المواعيد', en: 'Appointment cancellations', count: 18, pct: 30 },
                { ar: 'عدم تحسن', en: 'No improvement', count: 14, pct: 23 },
                { ar: 'وقت الانتظار', en: 'Wait time', count: 12, pct: 20 },
                { ar: 'التسعير', en: 'Pricing', count: 10, pct: 17 },
                { ar: 'أخرى', en: 'Other', count: 6, pct: 10 },
              ],
              derma: [
                { ar: 'نتائج غير مرضية', en: 'Unsatisfactory results', count: 16, pct: 27 },
                { ar: 'تورم بعد الجلسة', en: 'Post-session swelling', count: 14, pct: 23 },
                { ar: 'أسعار مرتفعة', en: 'High prices', count: 12, pct: 20 },
                { ar: 'وقت الانتظار', en: 'Wait time', count: 10, pct: 17 },
                { ar: 'أخرى', en: 'Other', count: 8, pct: 13 },
              ],
              tourism: [
                { ar: 'فنادق لا تطابق الوصف', en: 'Hotels not matching description', count: 18, pct: 30 },
                { ar: 'تأخير التأشيرات', en: 'Visa processing delays', count: 14, pct: 23 },
                { ar: 'إلغاء رحلات', en: 'Trip cancellations', count: 12, pct: 20 },
                { ar: 'مرشد سياحي غائب', en: 'Tour guide no-show', count: 10, pct: 17 },
                { ar: 'أخرى', en: 'Other', count: 6, pct: 10 },
              ],
              saas: [
                { ar: 'انقطاع أجهزة POS', en: 'Terminals offline', count: 22, pct: 37 },
                { ar: 'تأخر Settlement للبنك', en: 'Settlement delays to bank', count: 14, pct: 23 },
                { ar: 'تفعيل Apple Pay متأخر', en: 'Apple Pay activation delays', count: 10, pct: 17 },
                { ar: 'أخطاء بوابة الدفع الإلكتروني', en: 'Payment gateway errors', count: 8, pct: 13 },
                { ar: 'أخرى', en: 'Other', count: 6, pct: 10 },
              ],
            })[industry.id] || [
              { ar: 'عدم الرد', en: 'No response', count: 18, pct: 30 },
              { ar: 'تأخير الخدمة', en: 'Service delays', count: 15, pct: 25 },
              { ar: 'جودة الخدمة', en: 'Service quality', count: 12, pct: 20 },
              { ar: 'التسعير', en: 'Pricing', count: 10, pct: 17 },
              { ar: 'أخرى', en: 'Other', count: 5, pct: 8 },
            ]).map((topic, i) => (
              <div key={i} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 3 }}>
                  <span style={{ color: '#374151' }}>{lang === 'ar' ? topic.ar : topic.en}</span>
                  <span className="font-mono" style={{ color: '#EF4444', fontSize: 10 }}>{topic.count}</span>
                </div>
                <div style={{ height: 4, background: '#FEE2E2', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${topic.pct}%`, background: '#EF4444', borderRadius: 2, opacity: 0.7 }} />
                </div>
              </div>
            ))}
          </div>

          {/* NPS & Scores */}
          <div>
            <div style={{ fontSize: 12, color: '#6B7280', fontWeight: 600, marginBottom: 12 }}>
              {lang === 'ar' ? 'مؤشرات الرضا' : 'Satisfaction Scores'}
            </div>
            {[
              { label: lang === 'ar' ? 'التوصية' : 'Referral Score', value: '+67', color: '#22C55E', desc: lang === 'ar' ? 'ممتاز' : 'Excellent' },
              { label: lang === 'ar' ? 'الرضا' : 'Satisfaction', value: '92%', color: '#22C55E', desc: lang === 'ar' ? 'أعلى من المعيار' : 'Above benchmark' },
              { label: lang === 'ar' ? 'السهولة' : 'Ease Score', value: '1.8', color: '#22C55E', desc: lang === 'ar' ? 'سهولة عالية' : 'Low effort' },
              { label: lang === 'ar' ? 'حل فوري' : 'Resolved First Time', value: '87%', color: '#1B6B93', desc: lang === 'ar' ? 'حل من أول تواصل' : 'First contact resolution' },
            ].map((score, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '8px 10px', background: '#FAFAFA', borderRadius: 6, marginBottom: 6, border: '1px solid #F3F4F6'
              }}>
                <div>
                  <span className="font-mono" style={{ fontSize: 10, color: '#6B7280', fontWeight: 600 }}>{score.label}</span>
                  <div style={{ fontSize: 10, color: '#9CA3AF' }}>{score.desc}</div>
                </div>
                <span className="font-mono" style={{ fontSize: 18, fontWeight: 700, color: score.color }}>{score.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Row 6: Business Insights (full width) */}
      <div style={{
        background: 'linear-gradient(135deg, #1A1A2E 0%, #2D1B4E 100%)', borderRadius: 12, padding: '24px',
        marginBottom: 20, animation: 'slideUp 0.4s ease 0.9s both'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
          <IconChartBar size={18} color="#B794D4" stroke={1.8} />
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0, color: '#fff' }}>
            {lang === 'ar' ? 'ذكاء الأعمال' : 'Business Intelligence'}
          </h3>
          <span style={{ fontSize: 10, color: '#7B4FAE', background: '#5B2D8E33', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>
            {lang === 'ar' ? 'تحديث مباشر' : 'LIVE'}
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          {[
            {
              icon: IconReportMoney, color: '#22C55E',
              titleAr: 'الإيرادات المتوقعة', titleEn: 'Projected Revenue',
              valueAr: '٦.٨ مليون', valueEn: '6.8M SAR',
              descAr: 'بناءً على ١٢ صفقة مفتوحة بنسبة إغلاق ٧٢٪', descEn: 'Based on 12 open deals at 72% close rate',
              trendAr: '↑ ٢.١ مليون عن الشهر الماضي', trendEn: '↑ 2.1M vs last month', trendUp: true
            },
            {
              icon: IconUsers, color: '#1B6B93',
              titleAr: 'تكلفة الاستحواذ', titleEn: 'Customer Acquisition Cost',
              valueAr: '١٢٠ ريال', valueEn: '120 SAR',
              descAr: 'واتساب أقل تكلفة بـ ٦٥٪ مقارنة بالاتصال', descEn: 'WhatsApp 65% cheaper than cold calling',
              trendAr: '↓ ٤٥٪ بعد تفعيل AIDA', trendEn: '↓ 45% since AIDA activation', trendUp: false
            },
            {
              icon: IconClock, color: '#F59E0B',
              titleAr: 'دورة البيع', titleEn: 'Sales Cycle',
              valueAr: '٤.٢ أيام', valueEn: '4.2 days',
              descAr: 'من أول محادثة إلى إغلاق الصفقة', descEn: 'From first conversation to deal close',
              trendAr: '↓ ٦٠٪ عن المعدل اليدوي (١١ يوم)', trendEn: '↓ 60% from manual avg (11 days)', trendUp: false
            },
            {
              icon: IconTargetArrow, color: '#5B2D8E',
              titleAr: 'معدل التحويل', titleEn: 'Conversion Rate',
              valueAr: '٣٤٪', valueEn: '34%',
              descAr: 'من استفسار إلى عميل فعلي', descEn: 'From inquiry to paying customer',
              trendAr: '↑ ١٨٪ عن متوسط القطاع (١٦٪)', trendEn: '↑ 18% above industry avg (16%)', trendUp: true
            },
          ].map((bi, i) => {
            const Icon = bi.icon;
            return (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.06)', borderRadius: 10, padding: '16px',
                border: '1px solid rgba(255,255,255,0.08)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <Icon size={16} color={bi.color} stroke={1.8} />
                  <span style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 600 }}>
                    {lang === 'ar' ? bi.titleAr : bi.titleEn}
                  </span>
                </div>
                <div className="font-mono" style={{ fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 6 }}>
                  {lang === 'ar' ? bi.valueAr : bi.valueEn}
                </div>
                <div style={{ fontSize: 11, color: '#9CA3AF', lineHeight: 1.5, marginBottom: 8 }}>
                  {lang === 'ar' ? bi.descAr : bi.descEn}
                </div>
                <div style={{
                  fontSize: 11, fontWeight: 600, color: bi.trendUp ? '#22C55E' : '#60A5FA',
                  display: 'flex', alignItems: 'center', gap: 4
                }}>
                  {bi.trendUp ? <IconArrowUpRight size={12} /> : <IconArrowDownRight size={12} />}
                  {lang === 'ar' ? bi.trendAr : bi.trendEn}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Row 7: AI Recommendations (full width) */}
      <div style={{
        background: '#fff', borderRadius: 12, padding: '20px', border: '1px solid #E5E7EB',
        marginBottom: 20, animation: 'slideUp 0.4s ease 0.95s both'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
          <IconTargetArrow size={18} color="#5B2D8E" stroke={1.8} />
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>
            {lang === 'ar' ? 'توصيات ذكية للنمو' : 'AI Growth Recommendations'}
          </h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          {(({
            real_estate: [
              { icon: IconTrendingUp, color: '#22C55E', priority: lang === 'ar' ? 'عالي' : 'HIGH', titleAr: 'استهدف "المتصفحين" بحملة متابعة', titleEn: 'Target "Browsers" with follow-up campaign', descAr: '٤١ عميل شاف العروض بدون تواصل. حملة واتساب ممكن تحول ١٢-١٥ منهم', descEn: '41 leads viewed offers without engaging. WhatsApp campaign could convert 12-15', actionAr: 'تنفيذ عبر لطيفة', actionEn: 'Execute via Latifa' },
              { icon: IconAlertTriangle, color: '#EF4444', priority: lang === 'ar' ? 'عاجل' : 'URGENT', titleAr: 'غيّر مقاول الصيانة — شكاوى متكررة', titleEn: 'Replace maintenance contractor — repeated complaints', descAr: '٨ شكاوى تأخير هالأسبوع من نفس المقاول. رضا العملاء انخفض ١٨٪', descEn: '8 delay complaints this week from same contractor. Satisfaction dropped 18%', actionAr: 'تصعيد للإدارة', actionEn: 'Escalate to management' },
              { icon: IconCalendarEvent, color: '#1B6B93', priority: lang === 'ar' ? 'متوسط' : 'MEDIUM', titleAr: 'حط خطط الدفع بدل الأسعار الكاملة', titleEn: 'Show payment plans instead of full prices', descAr: 'الزوار يطلعون عند قسم الأسعار (٦٨٪ ارتداد). خطط الدفع تقلل الارتداد ٢٠٪', descEn: 'Visitors drop off at pricing (68% bounce). Payment plans reduce bounce by 20%', actionAr: 'تنفيذ تلقائي', actionEn: 'Enable automation' },
              { icon: IconReportMoney, color: '#F59E0B', priority: lang === 'ar' ? 'متوسط' : 'MEDIUM', titleAr: 'برنامج إحالة للعملاء الراضين', titleEn: 'Referral program for satisfied customers', descAr: 'أغلب عملاءك جاهزين يرشحونك. خصم ١٠٪ ممكن يجيب ٨-١٢ عميل شهرياً', descEn: 'Most customers would refer you. 10% discount could bring 8-12 new leads/month', actionAr: 'تصميم عبر لطيفة', actionEn: 'Design via Latifa' },
            ],
            cleaning: [
              { icon: IconTrendingUp, color: '#22C55E', priority: lang === 'ar' ? 'عالي' : 'HIGH', titleAr: 'حوّل عملاء المرة الواحدة لاشتراكات', titleEn: 'Convert one-time clients to subscriptions', descAr: 'عرض خصم بعد أول خدمة بيومين يحول ٣٠٪ منهم لاشتراكات شهرية', descEn: 'Offer discount 2 days after first service — converts 30% to monthly subscriptions', actionAr: 'تنفيذ عبر لطيفة', actionEn: 'Execute via Latifa' },
              { icon: IconAlertTriangle, color: '#EF4444', priority: lang === 'ar' ? 'عاجل' : 'URGENT', titleAr: 'فريق التنظيف ٣ — شكاوى جودة متكررة', titleEn: 'Cleaning crew #3 — repeated quality complaints', descAr: '٣ شكاوى هالأسبوع من نفس الفريق. يحتاج تدريب أو استبدال', descEn: '3 complaints this week from same crew. Needs training or replacement', actionAr: 'تصعيد للإدارة', actionEn: 'Escalate to management' },
              { icon: IconCalendarEvent, color: '#1B6B93', priority: lang === 'ar' ? 'متوسط' : 'MEDIUM', titleAr: 'فعّل حملة استرجاع العملاء المنقطعين', titleEn: 'Activate churned client recovery campaign', descAr: '٤١ عميل انقطع. حملة "وحشتنا" بخصم ١٥٪ ترجّع ١٠-١٢ منهم', descEn: '41 churned clients. "We miss you" campaign with 15% off recovers 10-12', actionAr: 'تنفيذ عبر لطيفة', actionEn: 'Execute via Latifa' },
              { icon: IconReportMoney, color: '#F59E0B', priority: lang === 'ar' ? 'متوسط' : 'MEDIUM', titleAr: 'أضف خدمة تنظيف المكيفات كإضافة', titleEn: 'Add AC cleaning as an upsell', descAr: '٤٥٪ من العملاء يسألون عن المكيفات. إضافتها كخيار ترفع متوسط الطلب ٤٠٪', descEn: '45% of clients ask about AC cleaning. Adding as option increases avg order 40%', actionAr: 'تفعيل تلقائي', actionEn: 'Enable automation' },
            ],
            car_dealership: [
              { icon: IconTrendingUp, color: '#22C55E', priority: lang === 'ar' ? 'عالي' : 'HIGH', titleAr: 'حاسبة تقسيط في أول صفحة الهبوط', titleEn: 'Financing calculator at top of landing page', descAr: '٧٠٪ من العملاء يسألون عن التقسيط أول شي. الحاسبة تختصر وقت البيع ٥٠٪', descEn: '70% of leads ask about financing first. Calculator cuts sales time by 50%', actionAr: 'تنفيذ تلقائي', actionEn: 'Enable automation' },
              { icon: IconAlertTriangle, color: '#EF4444', priority: lang === 'ar' ? 'عاجل' : 'URGENT', titleAr: 'تأخير صيانة — ٥ شكاوى هالأسبوع', titleEn: 'Maintenance delays — 5 complaints this week', descAr: 'قسم الصيانة متأخر بالمواعيد. الرضا انخفض ١٢٪ عن الشهر الماضي', descEn: 'Service dept running behind schedule. Satisfaction down 12% from last month', actionAr: 'تصعيد للإدارة', actionEn: 'Escalate to management' },
              { icon: IconCalendarEvent, color: '#1B6B93', priority: lang === 'ar' ? 'متوسط' : 'MEDIUM', titleAr: 'حملة متابعة لتجارب القيادة', titleEn: 'Follow-up campaign for test drives', descAr: '١٢ عميل جرّب بدون شراء. متابعة بعد ٤٨ ساعة تغلق ٢٥٪ منهم', descEn: '12 test drives without purchase. 48hr follow-up closes 25% of them', actionAr: 'تنفيذ عبر لطيفة', actionEn: 'Execute via Latifa' },
              { icon: IconReportMoney, color: '#F59E0B', priority: lang === 'ar' ? 'متوسط' : 'MEDIUM', titleAr: 'عروض تبديل السيارة القديمة', titleEn: 'Trade-in offer campaign', descAr: 'عملاء سابقين عندهم سيارات عمرها ٣+ سنوات. عرض تبديل يزيد المبيعات ١٥٪', descEn: 'Past customers with 3+ year old cars. Trade-in offer increases sales 15%', actionAr: 'تصميم عبر لطيفة', actionEn: 'Design via Latifa' },
            ],
            insurance: [
              { icon: IconTrendingUp, color: '#22C55E', priority: lang === 'ar' ? 'عالي' : 'HIGH', titleAr: 'حملة تجديد للوثائق القريبة من الانتهاء', titleEn: 'Renewal campaign for expiring policies', descAr: '٥٦ وثيقة تنتهي خلال ٣٠ يوم. حملة تذكير تحافظ على ٨٥٪ منهم', descEn: '56 policies expiring in 30 days. Reminder campaign retains 85% of them', actionAr: 'تنفيذ عبر لطيفة', actionEn: 'Execute via Latifa' },
              { icon: IconAlertTriangle, color: '#EF4444', priority: lang === 'ar' ? 'عاجل' : 'URGENT', titleAr: 'بطء معالجة المطالبات — رضا منخفض', titleEn: 'Slow claim processing — low satisfaction', descAr: 'متوسط المعالجة ١٤ يوم. المنافسين ٧ أيام. ٣ عملاء هددوا بالإلغاء', descEn: 'Avg processing 14 days. Competitors do 7. 3 clients threatened cancellation', actionAr: 'تصعيد للإدارة', actionEn: 'Escalate to management' },
              { icon: IconCalendarEvent, color: '#1B6B93', priority: lang === 'ar' ? 'متوسط' : 'MEDIUM', titleAr: 'جدول مقارنة التغطيات بصفحة الهبوط', titleEn: 'Coverage comparison table on landing page', descAr: '٦٥٪ من الزوار يقارنون الأسعار. جدول واضح يرفع التحويل ٢٠٪', descEn: '65% of visitors compare prices. Clear table increases conversions 20%', actionAr: 'تنفيذ تلقائي', actionEn: 'Enable automation' },
              { icon: IconReportMoney, color: '#F59E0B', priority: lang === 'ar' ? 'متوسط' : 'MEDIUM', titleAr: 'باقة تأمين شامل + صحي بسعر مخفض', titleEn: 'Bundle: comprehensive + health at discount', descAr: '٤٠٪ من عملاء التأمين الشامل يسألون عن الصحي. باقة مجمعة ترفع متوسط الوثيقة ٦٠٪', descEn: '40% of comprehensive clients ask about health. Bundle increases avg policy 60%', actionAr: 'تصميم عبر لطيفة', actionEn: 'Design via Latifa' },
            ],
            retail: [
              { icon: IconTrendingUp, color: '#22C55E', priority: lang === 'ar' ? 'عالي' : 'HIGH', titleAr: 'حملة استرجاع السلات المتروكة', titleEn: 'Abandoned cart recovery campaign', descAr: '٨٩ سلة متروكة هالأسبوع. حملة بعد ساعة من الترك ترفع التحويل ٢٥٪', descEn: '89 abandoned carts this week. 1-hour follow-up increases recovery 25%', actionAr: 'تنفيذ عبر لطيفة', actionEn: 'Execute via Latifa' },
              { icon: IconAlertTriangle, color: '#EF4444', priority: lang === 'ar' ? 'عاجل' : 'URGENT', titleAr: 'تأخر الشحن — شكاوى مرتفعة', titleEn: 'Shipping delays — complaints rising', descAr: 'شركة الشحن متأخرة بـ٤ طلبات. رضا العملاء انخفض ١٥٪', descEn: 'Courier delayed on 4 orders. Customer satisfaction down 15%', actionAr: 'تصعيد للإدارة', actionEn: 'Escalate to management' },
              { icon: IconCalendarEvent, color: '#1B6B93', priority: lang === 'ar' ? 'متوسط' : 'MEDIUM', titleAr: 'برنامج VIP للعملاء المتكررين', titleEn: 'VIP program for repeat customers', descAr: '٢٣ عميل يشتري أكثر من ٣ مرات شهرياً. برنامج ولاء يرفع التكرار ٣٠٪', descEn: '23 customers buy 3+ times monthly. Loyalty program increases frequency 30%', actionAr: 'تنفيذ تلقائي', actionEn: 'Enable automation' },
              { icon: IconReportMoney, color: '#F59E0B', priority: lang === 'ar' ? 'متوسط' : 'MEDIUM', titleAr: 'عروض "اشتري ٢ واحصل على خصم"', titleEn: 'Buy 2, get discount offers', descAr: 'متوسط الطلب منتج واحد. عرض الكمية يرفع متوسط الطلب ٤٥٪', descEn: 'Avg order is 1 item. Quantity offer increases avg order 45%', actionAr: 'تصميم عبر لطيفة', actionEn: 'Design via Latifa' },
            ],
            furniture: [
              { icon: IconTrendingUp, color: '#22C55E', priority: lang === 'ar' ? 'عالي' : 'HIGH', titleAr: 'زر واتساب عائم بصفحة الكنب', titleEn: 'Floating WhatsApp button on sofa page', descAr: 'الزوار يقضون وقت طويل بصفحة الكنب بدون تواصل. الزر يرفع الاستفسارات ٤٠٪', descEn: 'Visitors spend long time on sofa page without contacting. Button increases inquiries 40%', actionAr: 'تنفيذ تلقائي', actionEn: 'Enable automation' },
              { icon: IconAlertTriangle, color: '#EF4444', priority: lang === 'ar' ? 'عاجل' : 'URGENT', titleAr: 'تأخير توصيل المصنع — ٤ طلبات', titleEn: 'Factory delivery delay — 4 orders', descAr: 'المصنع متأخر أسبوع على ٤ طلبات. العملاء بدأوا يشتكون', descEn: 'Factory 1 week late on 4 orders. Customers starting to complain', actionAr: 'تصعيد للإدارة', actionEn: 'Escalate to management' },
              { icon: IconCalendarEvent, color: '#1B6B93', priority: lang === 'ar' ? 'متوسط' : 'MEDIUM', titleAr: 'خدمة استشارة تصميم مجانية', titleEn: 'Free design consultation service', descAr: '٦٠٪ من العملاء يحتاجون مساعدة بالاختيار. استشارة مجانية تزيد المبيعات ٢٥٪', descEn: '60% of clients need help choosing. Free consultation increases sales 25%', actionAr: 'تنفيذ عبر عبدالله', actionEn: 'Execute via Abdullah' },
              { icon: IconReportMoney, color: '#F59E0B', priority: lang === 'ar' ? 'متوسط' : 'MEDIUM', titleAr: 'باقة تأثيث غرفة كاملة بسعر مخفض', titleEn: 'Full room furnishing bundle at discount', descAr: 'العملاء يشترون قطعة وحدة. باقة الغرفة الكاملة ترفع متوسط الصفقة ٣ أضعاف', descEn: 'Clients buy single pieces. Full room bundle triples avg deal value', actionAr: 'تصميم عبر لطيفة', actionEn: 'Design via Latifa' },
            ],
            fnb: [
              { icon: IconTrendingUp, color: '#22C55E', priority: lang === 'ar' ? 'عالي' : 'HIGH', titleAr: 'حملة تموين أسبوعي للشركات', titleEn: 'Weekly corporate catering campaign', descAr: '١٨ عميل تموين متكرر. عقد أسبوعي يضمن إيراد ثابت ١٢ ألف/شهر', descEn: '18 repeat catering clients. Weekly contract guarantees 12K/mo steady revenue', actionAr: 'تنفيذ عبر لطيفة', actionEn: 'Execute via Latifa' },
              { icon: IconAlertTriangle, color: '#EF4444', priority: lang === 'ar' ? 'عاجل' : 'URGENT', titleAr: 'شكاوى التوصيل المسائي ارتفعت ٢٠٪', titleEn: 'Evening delivery complaints up 20%', descAr: 'التغليف الحراري غير كافي. يحتاج تحسين فوري قبل فقدان عملاء', descEn: 'Thermal packaging insufficient. Needs immediate improvement before losing clients', actionAr: 'تصعيد للإدارة', actionEn: 'Escalate to management' },
              { icon: IconCalendarEvent, color: '#1B6B93', priority: lang === 'ar' ? 'متوسط' : 'MEDIUM', titleAr: 'أضف تتبع مباشر للطلبات', titleEn: 'Add live order tracking', descAr: '٣٥٪ من المكالمات "وين طلبي؟". تتبع مباشر يقلل الاتصالات ٦٠٪', descEn: '35% of calls are "where is my order?". Live tracking reduces calls 60%', actionAr: 'تنفيذ تلقائي', actionEn: 'Enable automation' },
              { icon: IconReportMoney, color: '#F59E0B', priority: lang === 'ar' ? 'متوسط' : 'MEDIUM', titleAr: 'قائمة رمضان بأسعار خاصة', titleEn: 'Ramadan menu with special pricing', descAr: 'رمضان أعلى موسم. قائمة مخصصة مع حجز مبكر ترفع الإيراد ٤٥٪', descEn: 'Ramadan is peak season. Custom menu with early booking increases revenue 45%', actionAr: 'تصميم عبر لطيفة', actionEn: 'Design via Latifa' },
            ],
            physio: [
              { icon: IconTrendingUp, color: '#22C55E', priority: lang === 'ar' ? 'عالي' : 'HIGH', titleAr: 'حملة استرجاع المرضى المنقطعين', titleEn: 'Dropped patient recovery campaign', descAr: '٣٤ مريض انقطع. حملة تذكير بفوائد إكمال العلاج ترجّع ٤٠٪', descEn: '34 patients dropped. Reminder about treatment benefits recovers 40%', actionAr: 'تنفيذ عبر لطيفة', actionEn: 'Execute via Latifa' },
              { icon: IconAlertTriangle, color: '#EF4444', priority: lang === 'ar' ? 'عاجل' : 'URGENT', titleAr: 'إلغاء مواعيد متكرر — خسارة إيراد', titleEn: 'Frequent cancellations — revenue loss', descAr: '١٢ إلغاء هالأسبوع بدون إشعار مسبق. سياسة إلغاء واضحة تقلل الخسارة ٥٠٪', descEn: '12 no-show cancellations this week. Clear cancellation policy reduces loss 50%', actionAr: 'تصعيد للإدارة', actionEn: 'Escalate to management' },
              { icon: IconCalendarEvent, color: '#1B6B93', priority: lang === 'ar' ? 'متوسط' : 'MEDIUM', titleAr: 'شراكة مع أطباء للإحالات', titleEn: 'Doctor referral partnership program', descAr: 'إحالة طبية واحدة جابت ٣ مرضى. برنامج رسمي ممكن يضاعف المرضى الجدد', descEn: 'One doctor referral brought 3 patients. Formal program could double new patients', actionAr: 'تنفيذ عبر عبدالله', actionEn: 'Execute via Abdullah' },
              { icon: IconReportMoney, color: '#F59E0B', priority: lang === 'ar' ? 'متوسط' : 'MEDIUM', titleAr: 'باقات جلسات بخصم للالتزام', titleEn: 'Discounted session packages for commitment', descAr: 'معظم المرضى يحجزون جلسة وحدة. باقة ١٠ جلسات بخصم ٢٠٪ تضمن الالتزام', descEn: 'Most patients book single sessions. 10-session package at 20% off ensures commitment', actionAr: 'تصميم عبر لطيفة', actionEn: 'Design via Latifa' },
            ],
            derma: [
              { icon: IconTrendingUp, color: '#22C55E', priority: lang === 'ar' ? 'عالي' : 'HIGH', titleAr: 'متابعة الاستشارات غير المكتملة', titleEn: 'Follow up on incomplete consultations', descAr: '٤٥ استشارة بدون حجز. متابعة بعد ٤٨ ساعة ترفع الحجز ٣٥٪', descEn: '45 consultations without booking. 48hr follow-up increases booking 35%', actionAr: 'تنفيذ عبر لطيفة', actionEn: 'Execute via Latifa' },
              { icon: IconAlertTriangle, color: '#EF4444', priority: lang === 'ar' ? 'عاجل' : 'URGENT', titleAr: 'نتائج غير مرضية — شكاوى ما بعد الجلسات', titleEn: 'Unsatisfactory results — post-session complaints', descAr: '٣ شكاوى هالأسبوع عن نتائج الليزر. يحتاج مراجعة البروتوكول', descEn: '3 complaints about laser results this week. Protocol review needed', actionAr: 'تصعيد للإدارة', actionEn: 'Escalate to management' },
              { icon: IconCalendarEvent, color: '#1B6B93', priority: lang === 'ar' ? 'متوسط' : 'MEDIUM', titleAr: 'محتوى "قبل وبعد" للتسويق', titleEn: '"Before & After" content for marketing', descAr: 'محتوى النتائج يرفع التحويل ٦٠٪. اطلب إذن العملاء الراضين للنشر', descEn: 'Results content increases conversion 60%. Get consent from satisfied clients to publish', actionAr: 'تنفيذ عبر لطيفة', actionEn: 'Execute via Latifa' },
              { icon: IconReportMoney, color: '#F59E0B', priority: lang === 'ar' ? 'متوسط' : 'MEDIUM', titleAr: 'باقة العناية الشاملة — ٣ خدمات بسعر ٢', titleEn: 'Complete care bundle — 3 services for price of 2', descAr: '٣٠٪ من العملاء يحتاجون أكثر من خدمة. الباقة ترفع متوسط الإيراد ٨٠٪', descEn: '30% of clients need multiple services. Bundle increases avg revenue 80%', actionAr: 'تصميم عبر لطيفة', actionEn: 'Design via Latifa' },
            ],
            pharmacy: [
              { icon: IconTrendingUp, color: '#22C55E', priority: lang === 'ar' ? 'عالي' : 'HIGH', titleAr: 'نظام تنبيه مخزون تلقائي', titleEn: 'Automated inventory alert system', descAr: '٣ أدوية أساسية نفدت هالأسبوع. تنبيه تلقائي يمنع ٨٠٪ من شكاوى النفاد', descEn: '3 essential medications went out of stock. Auto-alerts prevent 80% of stock-out complaints', actionAr: 'تنفيذ تلقائي', actionEn: 'Enable automation' },
              { icon: IconAlertTriangle, color: '#EF4444', priority: lang === 'ar' ? 'عاجل' : 'URGENT', titleAr: 'شكاوى نفاد الأدوية ارتفعت ٤٠٪', titleEn: 'Stock-out complaints up 40%', descAr: 'العملاء يشتكون من عدم توفر أدوية أساسية. يحتاج مراجعة سلسلة التوريد', descEn: 'Customers complaining about essential medication unavailability. Supply chain review needed', actionAr: 'تصعيد للإدارة', actionEn: 'Escalate to management' },
              { icon: IconCalendarEvent, color: '#1B6B93', priority: lang === 'ar' ? 'متوسط' : 'MEDIUM', titleAr: 'حملة تذكير إعادة الصرف التلقائية', titleEn: 'Automated refill reminder campaign', descAr: '٦٠٪ من المرضى ينسون إعادة الصرف. تذكير تلقائي يرفع الالتزام ٣٥٪', descEn: '60% of patients forget refills. Auto-reminders increase adherence 35%', actionAr: 'تنفيذ عبر لطيفة', actionEn: 'Execute via Latifa' },
              { icon: IconReportMoney, color: '#F59E0B', priority: lang === 'ar' ? 'متوسط' : 'MEDIUM', titleAr: 'باقة مكملات شهرية بسعر مخفض', titleEn: 'Monthly supplement bundle at discount', descAr: '٤٥٪ من العملاء يشترون فيتامينات متعددة. باقة شهرية ترفع متوسط الطلب ٥٠٪', descEn: '45% of customers buy multiple vitamins. Monthly bundle increases avg order 50%', actionAr: 'تصميم عبر لطيفة', actionEn: 'Design via Latifa' },
            ],
            tourism: [
              { icon: IconTrendingUp, color: '#22C55E', priority: lang === 'ar' ? 'عالي' : 'HIGH', titleAr: 'حملة حجز مبكر للصيف', titleEn: 'Early booking campaign for summer', descAr: '٢٤٠ عميل سافر السنة الماضية. حملة حجز مبكر بخصم ١٠٪ تحجز ٣٠٪ منهم قبل الموسم', descEn: '240 past travelers. Early booking campaign with 10% off books 30% before peak season', actionAr: 'تنفيذ عبر لطيفة', actionEn: 'Execute via Latifa' },
              { icon: IconAlertTriangle, color: '#EF4444', priority: lang === 'ar' ? 'عاجل' : 'URGENT', titleAr: 'غيّر وكيل فنادق جورجيا', titleEn: 'Replace Georgia hotel agent', descAr: '٤ شكاوى هالشهر من نفس الوكيل المحلي. السمعة في خطر. يحتاج تغيير فوري أو ضمانات مكتوبة', descEn: '4 complaints this month from same local agent. Reputation at risk. Needs immediate replacement or written guarantees', actionAr: 'تصعيد للإدارة', actionEn: 'Escalate to management' },
              { icon: IconCalendarEvent, color: '#1B6B93', priority: lang === 'ar' ? 'متوسط' : 'MEDIUM', titleAr: 'استبيان ما بعد الرحلة تلقائي', titleEn: 'Automated post-trip survey', descAr: 'فقط ١٥٪ من العملاء يقيّمون الرحلة. استبيان تلقائي بعد يومين يرفع التقييمات ٣ أضعاف', descEn: 'Only 15% of clients rate their trip. Auto-survey 2 days after triples review rate', actionAr: 'تنفيذ تلقائي', actionEn: 'Enable automation' },
              { icon: IconReportMoney, color: '#F59E0B', priority: lang === 'ar' ? 'متوسط' : 'MEDIUM', titleAr: 'برنامج إحالة "سافر مع صديق"', titleEn: '"Travel with a friend" referral program', descAr: '٦٥٪ من العملاء يرشحونك لأصدقائهم. برنامج إحالة بخصم ٥٪ يجيب ١٥-٢٠ حجز شهرياً', descEn: '65% of clients would refer you. 5% referral discount brings 15-20 bookings monthly', actionAr: 'تصميم عبر لطيفة', actionEn: 'Design via Latifa' },
            ],
            saas: [
              { icon: IconTrendingUp, color: '#22C55E', priority: lang === 'ar' ? 'عالي' : 'HIGH', titleAr: 'حملة تفعيل Apple Pay للتجار', titleEn: 'Merchant Apple Pay activation campaign', descAr: '٣٢٠ تاجر بدون Apple Pay. حملة تفعيل ترفع متوسط مبيعات الجهاز ٢٠٪ خلال شهر', descEn: '320 merchants without Apple Pay. Activation campaign lifts avg terminal sales 20% within a month', actionAr: 'تنفيذ عبر لطيفة', actionEn: 'Execute via Latifa' },
              { icon: IconAlertTriangle, color: '#EF4444', priority: lang === 'ar' ? 'عاجل' : 'URGENT', titleAr: 'انقطاع أجهزة POS — ٢٨ تاجر بجدة', titleEn: 'Terminals offline — 28 merchants in Jeddah', descAr: 'حادث شريحة 4G من شريك الاتصالات. تجار يخسرون مبيعات. تصعيد فوري + تفعيل واي-فاي احتياطي', descEn: '4G SIM incident from telecom partner. Merchants losing sales. Immediate escalation + Wi-Fi failover activation', actionAr: 'تصعيد للإدارة', actionEn: 'Escalate to management' },
              { icon: IconCalendarEvent, color: '#1B6B93', priority: lang === 'ar' ? 'متوسط' : 'MEDIUM', titleAr: 'ترقية تلقائية لتجار حجم عالي', titleEn: 'Auto-upgrade for high-volume merchants', descAr: '٨٤ تاجر تجاوزوا حد عمليات الباقة. ترقية تلقائية لـBusiness Pro ترفع MRR ٣٠٪', descEn: '84 merchants exceeded plan transaction limits. Auto-upgrade to Business Pro lifts MRR 30%', actionAr: 'تنفيذ تلقائي', actionEn: 'Enable automation' },
              { icon: IconReportMoney, color: '#F59E0B', priority: lang === 'ar' ? 'متوسط' : 'MEDIUM', titleAr: 'استرجاع التجار غير النشطين', titleEn: 'Win-back inactive merchants', descAr: '١٤٥ تاجر بدون عمليات ٣٠ يوم. عرض Settlement T+٠ + ٣ شهور بنفس السعر يسترجع ٢٥٪ منهم', descEn: '145 merchants with no transactions for 30d. T+0 settlement offer + 3 months at same price recovers 25%', actionAr: 'تصميم عبر لطيفة', actionEn: 'Design via Latifa' },
            ],
          })[industry.id] || [
            { icon: IconTrendingUp, color: '#22C55E', priority: lang === 'ar' ? 'عالي' : 'HIGH', titleAr: 'استهدف العملاء المحتملين بحملة متابعة', titleEn: 'Target leads with follow-up campaign', descAr: 'حملة واتساب مخصصة ممكن تحول ١٥٪ من المهتمين لعملاء', descEn: 'Personalized WhatsApp campaign could convert 15% of interested leads', actionAr: 'تنفيذ عبر لطيفة', actionEn: 'Execute via Latifa' },
            { icon: IconAlertTriangle, color: '#EF4444', priority: lang === 'ar' ? 'عاجل' : 'URGENT', titleAr: 'شكاوى متكررة — يحتاج تدخل فوري', titleEn: 'Repeated complaints — needs immediate action', descAr: 'نمط شكاوى مكتشف. رضا العملاء انخفض ١٨٪', descEn: 'Complaint pattern detected. Customer satisfaction down 18%', actionAr: 'تصعيد للإدارة', actionEn: 'Escalate to management' },
            { icon: IconCalendarEvent, color: '#1B6B93', priority: lang === 'ar' ? 'متوسط' : 'MEDIUM', titleAr: 'أتمتة الحجز من المحادثة', titleEn: 'Automate in-conversation booking', descAr: 'أتمتة الحجز تختصر ٣ دقائق/محادثة وترفع التحويل', descEn: 'Booking automation saves 3 min/conversation and increases conversions', actionAr: 'تنفيذ تلقائي', actionEn: 'Enable automation' },
            { icon: IconReportMoney, color: '#F59E0B', priority: lang === 'ar' ? 'متوسط' : 'MEDIUM', titleAr: 'برنامج إحالة للعملاء الحاليين', titleEn: 'Referral program for existing customers', descAr: 'أغلب عملاءك جاهزين يرشحونك. خصم بسيط يجيب عملاء بدون تكلفة', descEn: 'Most customers would refer you. Simple discount brings leads at zero cost', actionAr: 'تصميم عبر لطيفة', actionEn: 'Design via Latifa' },
          ]).map((rec, i) => {
            const Icon = rec.icon;
            const priorityBg = rec.color === '#EF4444' ? '#FEF2F2' : rec.color === '#22C55E' ? '#F0FDF4' : rec.color === '#1B6B93' ? '#EFF6FF' : '#FFFBEB';
            return (
              <div key={i} style={{
                padding: '16px', borderRadius: 10, border: `1px solid ${rec.color}22`,
                background: priorityBg
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Icon size={16} color={rec.color} stroke={1.8} />
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#1A1A2E' }}>
                      {lang === 'ar' ? rec.titleAr : rec.titleEn}
                    </span>
                  </div>
                  <span className="font-mono" style={{
                    fontSize: 9, fontWeight: 700, color: rec.color, background: rec.color + '22',
                    padding: '2px 8px', borderRadius: 10
                  }}>
                    {rec.priority}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: '#4B5563', lineHeight: 1.6, marginBottom: 10 }}>
                  {lang === 'ar' ? rec.descAr : rec.descEn}
                </div>
                <button onClick={() => sendDemoAction(
                  rec.actionAr, rec.actionEn, rec.descAr, rec.descEn
                )} style={{
                  fontSize: 11, fontWeight: 600, color: rec.color, background: 'transparent',
                  border: `1px solid ${rec.color}44`, borderRadius: 6, padding: '5px 12px', cursor: 'pointer',
                  fontFamily: 'inherit', transition: 'all 0.2s'
                }}>
                  {lang === 'ar' ? rec.actionAr : rec.actionEn}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Row 8: AI Insights + Recent Activity */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* AI Insights */}
        <div style={{
          background: '#fff', borderRadius: 12, padding: '20px', border: '1px solid #E5E7EB',
          animation: 'slideUp 0.4s ease 1s both'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <IconTargetArrow size={18} color="#5B2D8E" stroke={1.8} />
            <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>
              {lang === 'ar' ? 'رؤى سريعة' : 'Quick Insights'}
            </h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              {
                icon: IconTrendingUp, color: '#22C55E',
                ar: 'معدل التحويل ارتفع ٢٣٪ بعد تفعيل الردود التلقائية من عبدالله',
                en: 'Conversion rate up 23% after enabling Abdullah\'s auto-responses'
              },
              {
                icon: IconAlertTriangle, color: '#F59E0B',
                ar: '٣ عملاء محتملين بميزانيات عالية لم يتم المتابعة معهم خلال ٤٨ ساعة',
                en: '3 high-budget leads haven\'t been followed up in 48 hours'
              },
              {
                icon: IconCalendarEvent, color: '#1B6B93',
                ar: 'أفضل وقت للحملات: الثلاثاء والأربعاء ١٠-١٢ ص (معدل فتح ٤٥٪)',
                en: 'Best campaign time: Tue/Wed 10-12 AM (45% open rate)'
              },
              {
                icon: IconReportMoney, color: '#5B2D8E',
                ar: 'القيمة المتوقعة للفرص المفتوحة: ٦.٨ مليون ريال',
                en: 'Expected pipeline value: 6.8M SAR'
              },
              {
                icon: IconBrandWhatsapp, color: '#22C55E',
                ar: 'واتساب يحقق ٣x معدل تحويل أعلى من الإيميل',
                en: 'WhatsApp delivers 3x higher conversion than email'
              },
              {
                icon: IconUserCheck, color: '#7B2D5F',
                ar: '٨٧٪ من الشكاوى تُحل من أول تواصل بدون تصعيد',
                en: '87% of complaints resolved on first contact without escalation'
              },
            ].map((insight, i) => {
              const Icon = insight.icon;
              return (
                <div key={i} style={{
                  display: 'flex', gap: 10, padding: '10px 12px', background: '#FAFAFA',
                  borderRadius: 8, border: '1px solid #F3F4F6'
                }}>
                  <Icon size={16} color={insight.color} stroke={1.8} style={{ flexShrink: 0, marginTop: 2 }} />
                  <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
                    {lang === 'ar' ? insight.ar : insight.en}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent activity */}
        <div style={{
          background: '#fff', borderRadius: 12, padding: '20px', border: '1px solid #E5E7EB',
          animation: 'slideUp 0.4s ease 1.05s both'
        }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>{t.recentActivity}</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {activities.map((act, i) => {
              const agent = AGENTS[act.agent];
              return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 10, fontSize: 13,
                  padding: '6px 0', borderBottom: i < activities.length - 1 ? '1px solid #F3F4F6' : 'none'
                }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%', background: agent.color, flexShrink: 0
                  }} />
                  <span style={{ fontWeight: 600, color: agent.color, minWidth: 55, fontSize: 12 }}>
                    {lang === 'ar' ? agent.nameAr : agent.name}
                  </span>
                  <span style={{ color: '#374151', flex: 1, fontSize: 12 }}>
                    {lang === 'ar' ? act.textAr : act.textEn}
                  </span>
                  <span className="font-mono" style={{ color: '#9CA3AF', fontSize: 11 }}>{act.time}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
